import React, { useEffect, useState } from 'react';
import {
  Input,
  Button,
  Select,
  Form,
  UploadFile,
  Typography,
  message,
} from 'antd';
import { EditFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import ImageUpload from '../../../Common/AvatarUpload';
import { useAuth, User } from '../../../../store/auth';
import {
  useCountriesGetAll,
  useUserUpdateMe,
} from '../../../../hooks/api/createaccount';
import {
  CategoryAndSubCategory,
  useCategory,
} from '../../../../store/category';
import { IconChecked } from '../../../Common/Icon';
import JoinOurCommunity from '../../TippingArticles/JoinOurCommunity';
import { generateFileObjFromUrl } from '../../../Common/ImageUpload';

import styles from './index.module.scss';

const { TextArea } = Input;
const { Text } = Typography;
const { Option, OptGroup } = Select;

export default function ProfileEditContent(): React.ReactElement {
  const [form] = Form.useForm();
  const { user } = useAuth();
  const navigate = useNavigate();
  const userUpdate = useUserUpdateMe();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [avatarFileId, setAvatarFileId] = useState<string | null>(null);
  const [countryId, setCountryId] = useState<number | null>(null);
  const countriesGetAll = useCountriesGetAll();
  const { data, loading } = countriesGetAll;
  const [editableFields, setEditableFields] = useState({
    email: false,
    password: false,
  });
  const [categoryFields, setCategoryFields] = useState({
    categoryId: '',
    subCategoryId: '',
  });
  const [selectedValue, setSelectedValue] = useState<string | undefined>();
  const [selectedIcon, setSelectedIcon] = useState<string | undefined>();

  const { listAll } = useCategory();
  const getCategories = listAll.response?.data;

  const defaultCategoryOption: CategoryAndSubCategory = {
    id: 'f000',
    name: 'Worker',
    isActive: true,
    header: '',
    description: '',
    shortDescription: '',
    author: '',
    file: {
      id: 'default-file-id',
      originalName: 'Non-tipped Worker Icon',
      location:
        'https://tippingguide-public-dev.s3.amazonaws.com/files/0b2b4aa0-0b6e-11f0-b3ea-83eb9b9908e9.png',
      mimeType: 'image/png',
      createdAt: new Date().toISOString(),
    },
    subCategories: [
      {
        id: 'sub-fallback-id',
        header: 'Non-tipped Worker',
        isActive: true,
        description: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      },
    ],
  } as const;

  const allCategories: CategoryAndSubCategory[] = getCategories?.length
    ? [defaultCategoryOption, ...getCategories]
    : [defaultCategoryOption];

  const handleChange = (value: string) => {
    const selectedCategory = allCategories.find((cat) =>
      cat.subCategories.some((sub) => sub.header === value)
    );

    if (selectedCategory) {
      const selectedSubcategory = selectedCategory.subCategories.find(
        (sub) => sub.header === value
      );

      setSelectedValue(selectedSubcategory?.header);
      setSelectedIcon(selectedCategory.file?.location);
      setCategoryFields({
        categoryId: selectedCategory.id,
        subCategoryId: selectedSubcategory?.id || '',
      });
    }
  };
  const toggleField = (field: 'email' | 'password') => {
    setEditableFields((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };
  useEffect(() => {
    if (!userUpdate.loading && !userUpdate.error && userUpdate.data?.success) {
      message.success('Profile updated successfully!');
      navigate('/profile');
      window.location.reload();
    }
  }, [userUpdate.data, userUpdate.error, userUpdate.loading]);

  useEffect(() => {
    const subCategoryName = user?.subCategory?.header;
    const categoryName = user?.category?.name;

    if (subCategoryName && categoryName) {
      setSelectedValue(subCategoryName); // value = sub.header
      setSelectedIcon(user?.category?.file?.location);
      setCategoryFields({
        categoryId: user?.category?.id || '',
        subCategoryId: user?.subCategory?.id || '',
      });
    } else {
      setSelectedValue('Non-tipped Worker');
      if (defaultCategoryOption.file?.location) {
        setSelectedIcon(defaultCategoryOption.file.location);
      }
      setCategoryFields({
        categoryId: defaultCategoryOption.id,
        subCategoryId: defaultCategoryOption.subCategories[0].id,
      });
    }

    form.setFieldsValue({
      subCategory: subCategoryName || 'Non-tipped Worker',
    });

    handleInitialFileList(user);
  }, [user]);

  const handleSubmit = (values: any) => {
    const payload = {
      ...values,
      photoId: avatarFileId,
      categoryId:
        categoryFields.categoryId === 'f000' ? null : categoryFields.categoryId,
      subCategoryId:
        categoryFields.subCategoryId === 'sub-fallback-id'
          ? null
          : categoryFields.subCategoryId,
    };

    delete payload.subCategory;

    if (fileList.length === 0 && !avatarFileId) {
      payload.photoId = null;
    }
    if (fileList.length > 0 && !avatarFileId) {
      delete payload.photoId;
    }

    if (!values.password) {
      delete payload.password;
    }

    userUpdate.fetch(payload);
  };

  useEffect(() => {
    const htmlDescription = user?.description || '';

    const plainText = htmlDescription
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p><p>/gi, '\n\n')
      .replace(/<\/?[^>]+(>|$)/g, '');

    form.setFieldsValue({
      description: plainText,
    });
  }, []);

  const handleInitialFileList = (imageData: User | null) => {
    if (imageData?.photo) {
      const banner = generateFileObjFromUrl(imageData.photo.location);
      setFileList([banner]);
    }
  };
  function getLabelForSelected(
    categories: CategoryAndSubCategory[] | undefined,
    selectedSubHeader: string | undefined
  ): string | undefined {
    if (!categories || !selectedSubHeader) return;

    for (const cat of categories) {
      const sub = cat.subCategories.find((s) => s.header === selectedSubHeader);
      if (sub) return `${cat.name} / ${sub.header}`;
    }
  }
  return (
    <>
      <div className={styles.wrapper}>
        <div className={styles.container}>
          <div className={styles.profileWrapper}>
            <Form
              layout="vertical"
              form={form}
              onFinish={handleSubmit}
              initialValues={{
                email: user?.email || '',
                name: user?.name || '',
                industry: 'Restaurant / Dine-In',
                zipCode: user?.zipCode,
                countryId: user?.country?.id,
                description: user?.description,
              }}
            >
              <div className={styles.header}>
                <h2>Edit Profile</h2>
                <div className={styles.actionRow}>
                  <Button
                    type="text"
                    onClick={() => navigate('/profile')}
                    className={styles.Btn}
                  >
                    Cancel
                  </Button>
                  <Button
                    htmlType="submit"
                    type="primary"
                    className={styles.Btn}
                    icon={<IconChecked />}
                  >
                    Save Changes
                  </Button>
                </div>
              </div>

              <div className={styles.profileContainer}>
                <div className={styles.leftBlock}>
                  <h3 className={styles.blockTitle}>Your Photo</h3>
                  <span className={styles.titleInfo}>
                    This will be displayed on your profile
                  </span>
                  <div style={{ margin: '55px 0' }}>
                    <ImageUpload
                      name="photoId"
                      fileList={fileList}
                      setFileList={setFileList}
                      cropperProps={{ modalClassName: 'croper' }}
                      onUploadSuccess={(id) => setAvatarFileId(id)}
                    />
                  </div>
                  <Form.Item label="Email" name="email">
                    <Input
                      size="large"
                      disabled={!editableFields.email}
                      className={styles.changeBtn}
                      suffix={
                        <Button
                          icon={<EditFilled />}
                          type="text"
                          onClick={() => toggleField('email')}
                          style={{ padding: 0, color: '#00000066' }}
                        >
                          <p>{editableFields.email ? 'Cancel' : 'Change'}</p>
                        </Button>
                      }
                    />
                  </Form.Item>
                  <Form.Item label="Password" name="password">
                    <Input
                      size="large"
                      placeholder="Type here"
                      disabled={!editableFields.password}
                      className={styles.changeBtn}
                      suffix={
                        <Button
                          icon={<EditFilled />}
                          type="text"
                          onClick={() => toggleField('password')}
                          style={{ padding: 0, color: '#00000066' }}
                        >
                          <p>{editableFields.password ? 'Cancel' : 'Change'}</p>
                        </Button>
                      }
                    />
                  </Form.Item>
                </div>

                <div className={styles.rightBlock}>
                  <h3>Personal details</h3>
                  <span className={styles.titleInfo}>
                    This will be displayed on your profile
                  </span>
                  <Form.Item
                    label="Username"
                    name="name"
                    style={{ marginTop: '24px' }}
                  >
                    <Input
                      placeholder="Type here"
                      size="large"
                      className={styles.inp}
                    />
                  </Form.Item>

                  <Form.Item label="Industry / Occupation" name="subCategory">
                    <Select
                      className={styles.selectInp}
                      size="large"
                      style={{ height: 50 }}
                      onChange={handleChange}
                      value={selectedValue || 'Non-tipped Worker'}
                      placeholder="Industry / Occupation"
                      labelRender={() => (
                        <div className={styles.selectedCategory}>
                          {selectedIcon && (
                            <img
                              src={selectedIcon}
                              alt="Selected icon"
                              className={styles.icon}
                            />
                          )}
                          {getLabelForSelected(
                            allCategories,
                            selectedValue || 'Non-tipped Worker'
                          )}
                        </div>
                      )}
                    >
                      {allCategories.map((cat) => (
                        <OptGroup label={cat.name} key={cat.id}>
                          {cat.subCategories.map((sub) => (
                            <Option value={sub.header} key={sub.id}>
                              <div className={styles.selectedCategory}>
                                {cat.file?.location && (
                                  <img
                                    src={cat.file.location}
                                    alt="Selected icon"
                                    className={styles.icon}
                                  />
                                )}
                                {sub.header}
                              </div>
                            </Option>
                          ))}
                        </OptGroup>
                      ))}
                    </Select>
                  </Form.Item>

                  <Form.Item name="countryId" label="Your Country">
                    <Select
                      size="large"
                      className={styles.selectInp}
                      style={{ height: '50px' }}
                      showSearch
                      placeholder={loading ? 'Loading countries...' : 'Select'}
                      loading={loading}
                      options={data?.data?.map((country) => ({
                        value: country.id,
                        label: country.name,
                      }))}
                      onChange={(value) => setCountryId(value)}
                      filterOption={(input, option) =>
                        option?.label
                          ?.toLowerCase()
                          .includes(input.toLowerCase()) ?? false
                      }
                    />
                  </Form.Item>

                  <Form.Item label="Zip Code" name="zipCode">
                    <Input
                      placeholder="Type here"
                      size="large"
                      className={styles.inp}
                    />
                  </Form.Item>
                </div>
              </div>

              <div className={styles.aboutBlock}>
                <h3>About me</h3>
                <span className={styles.titleInfo}>
                  Write a short introduction.
                </span>
                <h4 style={{ marginTop: 30 }}>About me</h4>
                <Form.Item name="description" style={{ marginBottom: 8 }}>
                  <TextArea
                    placeholder="Enter your comment"
                    className={styles.description}
                    maxLength={1000}
                    rows={4}
                  />
                </Form.Item>
                <Text type="secondary">1000 characters left</Text>
              </div>
            </Form>
          </div>
        </div>
      </div>
      <JoinOurCommunity />
    </>
  );
}
